import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  Badge,
  SimpleGrid,
  useToast,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Alert,
  AlertIcon,
  AlertDescription,
  Tooltip,
} from '@chakra-ui/react';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import { idmApi } from '../config/api';

const IdmIntegrationData = ({ onImportProduct }) => {
  const toast = useToast();
  const [sourceType, setSourceType] = useState('nonquota');
  const [mbmid, setMbmid] = useState('0100000000000001');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isIssuingTicket, setIsIssuingTicket] = useState(false);
  const [products, setProducts] = useState([]);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [issueForm, setIssueForm] = useState({
    skuId: '',
    qty: '1',
    date: '',
    externalId: '',
  });

  const endpointHint = sourceType === 'quota'
    ? 'Source: IDM OTA (kuota) via PRIME Integrated adapter.'
    : sourceType === 'tmii'
      ? 'Source: TMII OTA Public API via PRIME Integrated adapter.'
      : 'Source: IDM external tariff (non-kuota) via PRIME Integrated adapter.';

  const handleError = (err) => {
    const message = err?.response?.data?.message || err.message || 'Request gagal';
    toast({
      title: 'Integration API Error',
      description: message,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  };

  const loadProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      const isTmii = sourceType === 'tmii';
      const isQuota = sourceType === 'quota';
      const endpoint = isTmii
        ? '/tmii/skus'
        : isQuota
          ? '/tariff/quota'
          : '/tariff/nonquota';
      const response = await idmApi.get(endpoint, {
        params: isQuota ? { mbmid } : {},
      });
      const rows = response?.data?.data?.tariff || [];
      setProducts(Array.isArray(rows) ? rows : []);
      if (Array.isArray(rows) && rows.length > 0 && isTmii) {
        setIssueForm((prev) => ({
          ...prev,
          skuId: prev.skuId || String(rows[0]?.id || ''),
        }));
      }
      setLastSyncAt(new Date());
      toast({
        title: `List produk ${isTmii ? 'TMII' : 'IDM'} diperbarui`,
        description: `${rows.length || 0} produk berhasil dimuat`,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [mbmid, sourceType, toast]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((item) => {
      const idText = item?.id != null ? String(item.id) : '';
      const name = String(item?.name || '').toLowerCase();
      const code = String(item?.code || '').toLowerCase();
      const currency = String(item?.currency || '').toLowerCase();
      return (
        idText.toLowerCase().includes(q) ||
        name.includes(q) ||
        code.includes(q) ||
        currency.includes(q)
      );
    });
  }, [products, search]);

  const handleImport = (item) => {
    if (typeof onImportProduct !== 'function') return;
    onImportProduct({
      ...item,
      importedAt: Date.now(),
    });
  };

  const handleIssueFormChange = (field, value) => {
    setIssueForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleIssueTicket = async () => {
    const qty = Number(issueForm.qty);
    if (!issueForm.skuId) {
      toast({
        title: 'SKU wajib dipilih',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    if (!Number.isFinite(qty) || qty <= 0) {
      toast({
        title: 'Qty harus lebih dari 0',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    if (!issueForm.date || !issueForm.externalId) {
      toast({
        title: 'Tanggal dan External ID wajib diisi',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    try {
      setIsIssuingTicket(true);
      const payload = {
        tickets: [{ skuId: issueForm.skuId, qty }],
        date: issueForm.date,
        externalId: issueForm.externalId,
      };
      const response = await idmApi.post('/tmii/tickets', payload);
      toast({
        title: 'Issue tiket TMII berhasil',
        description: response?.data?.message || 'Ticket berhasil di-issue',
        status: 'success',
        duration: 3500,
        isClosable: true,
      });
      setIssueForm((prev) => ({
        ...prev,
        externalId: '',
      }));
    } catch (err) {
      handleError(err);
    } finally {
      setIsIssuingTicket(false);
    }
  };

  return (
    <Box>
      <VStack align="stretch" spacing={4} mb={6}>
        <Heading size={{ base: 'md', lg: 'lg' }} color="gray.700">
          Destination Management - List Produk Integration
        </Heading>
        <Text fontSize="sm" color="gray.500">
          Daftar produk dari IDM atau TMII OTA yang siap digunakan sebagai referensi inventory Destination Management.
        </Text>
      </VStack>

      <Card bg="white" borderRadius="16px" boxShadow="sm" mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
            <FormControl>
              <FormLabel fontSize="sm">Sumber Tiket IDM</FormLabel>
              <Select value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
                <option value="nonquota">Non-kuota (Masuk Candi)</option>
                <option value="quota">Kuota OTA (Naik Candi / Sunrise / Sunset)</option>
                <option value="tmii">TMII OTA (Public API)</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">MBMID (khusus Kuota OTA)</FormLabel>
              <Input
                placeholder="0100000000000001"
                value={mbmid}
                onChange={(e) => setMbmid(e.target.value)}
                isDisabled={sourceType !== 'quota'}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Status</FormLabel>
              <HStack>
                <Badge colorScheme={sourceType === 'quota' ? 'orange' : sourceType === 'tmii' ? 'purple' : 'blue'}>
                  {sourceType === 'quota'
                    ? 'quota_ota'
                    : sourceType === 'tmii'
                      ? 'tmii_ota_public'
                      : 'nonquota_external'}
                </Badge>
                <Text fontSize="xs" color="gray.500">
                  {endpointHint}
                </Text>
              </HStack>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Cari Produk</FormLabel>
              <Input
                placeholder="Cari nama, kode, ID, currency"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </FormControl>
          </SimpleGrid>
          <HStack mt={4} justify="space-between" align="center">
            <HStack>
              <Button
                leftIcon={isLoading ? <Spinner size="sm" /> : <FiRefreshCw />}
                colorScheme="blue"
                onClick={loadProducts}
                isDisabled={isLoading}
              >
                Refresh List Produk
              </Button>
              <Badge colorScheme="green">{filteredProducts.length} produk</Badge>
            </HStack>
            <Text fontSize="xs" color="gray.500">
              Last sync: {lastSyncAt ? lastSyncAt.toLocaleString() : '-'}
            </Text>
          </HStack>
        </CardBody>
      </Card>

      <Alert status="info" borderRadius="md" mb={4}>
        <AlertIcon />
        <AlertDescription fontSize="sm">
          Produk di halaman ini adalah data sumber dari API provider. Klik "Import ke PRIME" untuk membuka form produk
          dengan data dasar terisi otomatis, lalu lengkapi metadata internal sebelum disimpan.
        </AlertDescription>
      </Alert>

      {sourceType === 'tmii' && (
        <Card bg="white" borderRadius="16px" boxShadow="sm" mb={4}>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Heading size="sm" color="gray.700">
                TMII Realtime Ticket Issue
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">SKU</FormLabel>
                  <Select
                    value={issueForm.skuId}
                    onChange={(e) => handleIssueFormChange('skuId', e.target.value)}
                  >
                    <option value="">Pilih SKU</option>
                    {products.map((item, idx) => (
                      <option key={`${item?.id || 'sku'}-${idx}`} value={item?.id ?? ''}>
                        {item?.name || item?.id || '-'}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Qty</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    value={issueForm.qty}
                    onChange={(e) => handleIssueFormChange('qty', e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Tanggal Kunjungan</FormLabel>
                  <Input
                    type="date"
                    value={issueForm.date}
                    onChange={(e) => handleIssueFormChange('date', e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">External ID</FormLabel>
                  <Input
                    placeholder="tmii-ord-0001"
                    value={issueForm.externalId}
                    onChange={(e) => handleIssueFormChange('externalId', e.target.value)}
                  />
                </FormControl>
              </SimpleGrid>
              <HStack justify="space-between" align="center">
                <Button
                  colorScheme="purple"
                  onClick={handleIssueTicket}
                  isDisabled={isIssuingTicket}
                  leftIcon={isIssuingTicket ? <Spinner size="sm" /> : undefined}
                >
                  Issue Ticket TMII
                </Button>
                <Text fontSize="xs" color="orange.600">
                  Redeem endpoint TMII belum aktif di vendor docs. Slot redeem akan diaktifkan setelah kontrak endpoint
                  resmi diberikan.
                </Text>
              </HStack>
              <HStack>
                <Button size="sm" variant="outline" isDisabled>
                  Redeem Ticket TMII (Pending Contract)
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      )}

      <Card bg="white" borderRadius="16px" boxShadow="sm">
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Heading size="sm" color="gray.700">
              Tabel Produk Integration
            </Heading>
            <TableContainer border="1px solid" borderColor="gray.100" borderRadius="md">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>No</Th>
                    <Th>ID</Th>
                    <Th>Nama Produk</Th>
                    <Th>Kode</Th>
                    <Th isNumeric>Harga</Th>
                    <Th>Currency</Th>
                    <Th>Aksi</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredProducts.length === 0 ? (
                    <Tr>
                      <Td colSpan={7}>
                        <Text textAlign="center" color="gray.500" py={3}>
                          Belum ada data produk. Klik "Refresh List Produk" untuk memuat data IDM.
                        </Text>
                      </Td>
                    </Tr>
                  ) : (
                    filteredProducts.map((item, idx) => (
                      <Tr key={`${item?.id || 'row'}-${idx}`}>
                        <Td>{idx + 1}</Td>
                        <Td>{item?.id ?? '-'}</Td>
                        <Td>{item?.name || '-'}</Td>
                        <Td>{item?.code || '-'}</Td>
                        <Td isNumeric>
                          {item?.price != null ? (
                            <Tooltip
                              label={
                                item?.priceTiers
                                  ? `Default: Rp${Number(item.price).toLocaleString('id-ID')} | Weekend: Rp${Number(item.priceTiers.weekend || 0).toLocaleString('id-ID')} | Holiday: Rp${Number(item.priceTiers.holiday || 0).toLocaleString('id-ID')}`
                                  : ''
                              }
                              isDisabled={!item?.priceTiers}
                              placement="top"
                            >
                              <Text as="span" cursor={item?.priceTiers ? 'help' : 'default'}>
                                Rp{Number(item.price).toLocaleString('id-ID')}
                                {item?.priceTiers ? (
                                  <Badge ml={1} size="xs" colorScheme="orange" fontSize="2xs">tier</Badge>
                                ) : null}
                              </Text>
                            </Tooltip>
                          ) : (
                            '-'
                          )}
                        </Td>
                        <Td>{item?.currency || '-'}</Td>
                        <Td>
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="outline"
                            leftIcon={<FiDownload />}
                            onClick={() => handleImport(item)}
                          >
                            Import ke PRIME
                          </Button>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default IdmIntegrationData;
